import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import { IssueGrid } from "@/components/sections/issue-grid";
import { PhotoHero, PhotoProcess, WorkGallery } from "@/components/sections/service-photo";
import { Droplets, TrendingDown, Snowflake, Wind, PoundSterling, Activity } from "lucide-react";
import { CheckCircle2 } from "lucide-react";

export default function Maintenance() {
  const issues = [
    { 
      id: "leak", 
      title: "Leaking Water", 
      icon: <Droplets className="w-6 h-6" />,
      desc: "Condensate drains can easily become blocked with dust and algae over time. Regular maintenance includes flushing these lines to prevent leaks that can damage your ceilings or walls.",
    },
    { 
      id: "performance", 
      title: "Loss of Performance", 
      icon: <TrendingDown className="w-6 h-6" />,
      desc: "A gradual decline in cooling or heating power usually points to dirty coils or low refrigerant. A service restores the unit to factory efficiency.",
    },
    { 
      id: "freezing", 
      title: "Prone to Freezing", 
      icon: <Snowflake className="w-6 h-6" />,
      desc: "If ice builds up on the indoor coil, it's often due to restricted airflow from a blocked filter. Routine cleaning prevents this system-stressing issue.",
    },
    { 
      id: "odour", 
      title: "Stale Odours", 
      icon: <Wind className="w-6 h-6" />,
      desc: "Unpleasant smells mean bacteria are growing in the drip tray or on the coil. We use specialist antibacterial foam cleaners during a service to eliminate odours and protect your health.",
    },
    { 
      id: "bills", 
      title: "Increased Energy Bills", 
      icon: <PoundSterling className="w-6 h-6" />,
      desc: "A dirty AC unit has to work up to 30% harder to cool a room. Keeping it clean through maintenance directly lowers your electricity consumption.",
    },
    { 
      id: "airflow", 
      title: "Poor Airflow", 
      icon: <Activity className="w-6 h-6" />,
      desc: "Clogged filters drastically reduce the volume of air the unit can circulate. We'll wash or replace your filters, ensuring optimal air distribution.",
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <PhotoHero src="project-17.webp" alt="Engineer holding a removed filter beside a rooftop air conditioning unit" position="65% 35%" overlay="bg-gradient-to-r from-emerald-950/90 via-emerald-900/65 to-slate-900/20" eyebrow="Filters · Coils · Drains · Checks" testId="hero-maintenance">
          <h1 className="text-4xl md:text-5xl font-bold font-display mb-4">Air Conditioning Maintenance London</h1>
          <p className="text-lg md:text-xl text-emerald-50 mb-6">
            Protect your investment, lower running costs, and ensure pristine air quality with our comprehensive servicing contracts.
          </p>
          <Button size="lg" variant="secondary" className="rounded-full h-14 px-8 text-lg font-bold text-emerald-800" onClick={() => document.getElementById('get-in-touch')?.scrollIntoView({ behavior: 'smooth' })}>
            Request a Maintenance Quote
          </Button>
        </PhotoHero>

        {/* What to look out for */}
        <section className="py-12 lg:py-16 bg-slate-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">What To Look Out For</h2>
              <p className="text-lg text-muted-foreground">Warning signs that your system is overdue for a service.</p>
            </div>

            <IssueGrid items={issues} tone="emerald" cta="Book a Service" testIdPrefix="issue" />
          </div>
        </section>

        {/* What a service includes */}
        <PhotoProcess
          testId="section-maintenance-process"
          title="What a Service Includes"
          intro="A service is a structured set of checks and cleans on both the indoor and outdoor units, recorded so changes can be spotted visit to visit."
          photo="project-25.webp" alt="Engineer holding a filter at a rooftop unit during a service" accent="bg-emerald-600" position="center 35%"
          caption="Filter removed for inspection and cleaning at a rooftop unit."
          steps={[
            { title: "Filters", desc: "Filters are removed, inspected, washed or replaced so the unit can move its full volume of air." },
            { title: "Coils", desc: "Indoor and outdoor coils are cleaned with suitable coil cleaner to restore heat transfer and reduce odours." },
            { title: "Drains & trays", desc: "Drip trays are cleaned and condensate lines flushed and tested to prevent leaks." },
            { title: "Electrical & mechanical", desc: "Connections, fans, mountings and casings are checked for wear, vibration or damage." },
            { title: "Performance checks", desc: "Operating pressures and air temperatures are recorded and compared with previous readings." },
          ]}
        />

        {/* Maintenance Gallery */}
        <WorkGallery
          testId="gallery-maintenance"
          title="Maintenance Visits in Pictures"
          intro="Photographs from our own servicing work on rooftop and ground-level equipment."
          items={[
            { src: "project-12.webp", alt: "Pressure gauges connected during a service", caption: "Recording system pressures as part of the service checks." },
            { src: "project-20.webp", alt: "Opened rooftop HVAC cabinet", caption: "Rooftop unit opened to inspect and clean internal components." },
            { src: "project-16.webp", alt: "Daikin outdoor units", caption: "Daikin outdoor units: coils and surrounding area kept clear for airflow." },
            { src: "project-22.webp", alt: "Rooftop outdoor unit", caption: "Rooftop outdoor plant where access and condition are checked." },
          ]}
        />

        {/* Benefits Section */}
        <section className="py-12 lg:py-16 bg-background border-t border-border">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-8">The Benefits of Regular Maintenance</h2>
            <div className="grid md:grid-cols-3 gap-4 text-left">
              <div className="p-5 bg-slate-50 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-3" />
                <h3 className="text-xl font-bold font-display mb-2">Extended Lifespan</h3>
                <p className="text-muted-foreground">Well-maintained systems last significantly longer before requiring expensive replacements, protecting your initial investment.</p>
              </div>
              <div className="p-5 bg-slate-50 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-3" />
                <h3 className="text-xl font-bold font-display mb-2">Lower Energy Bills</h3>
                <p className="text-muted-foreground">Clean coils and filters mean your system doesn't have to work as hard, dramatically reducing its electricity consumption.</p>
              </div>
              <div className="p-5 bg-slate-50 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-3" />
                <h3 className="text-xl font-bold font-display mb-2">F-Gas Compliance</h3>
                <p className="text-muted-foreground">For commercial clients, regular leak testing keeps you compliant with strict UK F-Gas regulations.</p>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
